import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseUrl, getDateWithDay, qtyToCrates, cratesToQty } from "../../comman/config";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../components/Loader.js";
import Select from 'react-select';
import { menuAboveTable } from "../../comman/selectMenu";

function DairyReturnList() {
    // Form state
    const today = new Date().toISOString().split("T")[0];
    const [mealDate, setMealDate] = useState(today);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedMenuItem, setSelectedMenuItem] = useState(null);
    const [selectedKits, setSelectedKits] = useState([]);
    // Which of the two boxes the storekeeper is counting in. The other one is filled from it, so
    // only one of them is ever typed into.
    const [returnSelection, setReturnSelection] = useState("theli");
    const [returnQty, setReturnQty] = useState("");
    const [crates, setCrates] = useState("");
    const [remarks, setRemarks] = useState("");
    const [activeIndex, setActiveIndex] = useState(null);

    // Data lists
    const [ingredientList, setIngredientList] = useState([]);
    const [sessionList, setSessionList] = useState([]);
    const [menuItemList, setMenuItemList] = useState([]);
    const [kitList, setKitList] = useState([]);

    // UI states
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        getSessionList();
        loadIngredientListForDate();
    }, [mealDate]);

    useEffect(() => {
        if (selectedIngredient && selectedSession) {
            loadMenuItems();
        } else {
            setMenuItemList([]);
            setSelectedMenuItem(null);
        }
    }, [selectedIngredient, selectedSession, mealDate]);

    useEffect(() => {
        if (selectedMenuItem) {
            loadKitNumbers();
        } else {
            setKitList([]);
            setSelectedKits([]);
        }
    }, [selectedMenuItem, selectedSession, selectedIngredient, mealDate]);

    // A different ingredient brings a different Std. Qty. per Crate, so the box that is being
    // filled in for the storekeeper is worked out again from the one they typed.
    useEffect(() => {
        syncQtyFields(returnSelection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIngredient]);

    const getSessionList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseUrl}/comman/list?table=session_master&condition=status=1`,
                { headers: { 'Cache-Control': 'no-cache' } }
            );
            setSessionList(response.data.data);
        } catch (error) {
            console.error("Error fetching sessions:", error);
            toast.error("Failed to load sessions");
        } finally {
            setLoading(false);
        }
    };

    const loadIngredientListForDate = async () => {
        setLoading(true);
        try {
            // Query item_requirement_summary for the selected date and order_status = 3
            const response = await axios.get(
                `${baseUrl}/ingredients/list?limit=all`
            );

            // Filter ingredients for dairy store (store = 2) that have order_status = 3 for the selected date
            const orderResponse = await axios.post(`${baseUrl}/dairy_order/list`, {
                from_date: mealDate,
                to_date: mealDate,
                order_status: "3" // Only ordered ingredients
            });

            // Get ingredient IDs that have been ordered
            const orderedIngredientIds = orderResponse.data.data.map(item => item.ingredient_id);

            // Filter the full ingredient list to only show dairy store ingredients that have been ordered
            const filteredList = response.data.data.filter(
                item => item.status == 1 &&
                    item?.store?.id == 2 &&
                    orderedIngredientIds.includes(item.id)
            );
            setIngredientList(filteredList);
        } catch (error) {
            console.error("Error fetching ingredients:", error);
            toast.error("Failed to load ingredients");
        } finally {
            setLoading(false);
        }
    };

    const loadMenuItems = async () => {
        setLoading(true);
        try {
            // Query item_requirement_detail to get menu items for the selected date, session, and ingredient
            const response = await axios.get(
                `${baseUrl}/dairy_return/menu_item`,
                {
                    params: {
                        condition: `ird.date = '${mealDate}' AND ird.ingredient_id = '${selectedIngredient.id}' AND ird.session = '${selectedSession.id}'`
                    }
                }
            );

            // Extract unique menu items
            const uniqueMenuItems = [];
            const seenMenuItemIds = new Set();

            response.data.data.forEach(item => {
                if (!seenMenuItemIds.has(item.item_id)) {
                    seenMenuItemIds.add(item.item_id);
                    uniqueMenuItems.push({
                        id: item.menu_item_id,
                        item_name: item.item_name
                    });
                }
            });

            setMenuItemList(uniqueMenuItems);
            console.log("menu item:", menuItemList);
        } catch (error) {
            console.error("Error fetching menu items:", error);
            toast.error("Failed to load menu items");
        } finally {
            setLoading(false);
        }
    };

    const loadKitNumbers = async () => {
        setLoading(true);
        try {
            // Query item_requirement_master to get kit numbers for the selected date, session, and menu item
            const response = await axios.get(
                `${baseUrl}/dairy_return/menu_item`,
                {
                    params: {
                        condition: `ird.date = '${mealDate}' AND ird.ingredient_id = '${selectedIngredient.id}' AND ird.session = '${selectedSession.id}' AND ird.menu_item_id = '${selectedMenuItem.id}'`
                    }
                }
            );

            // Extract unique kit numbers
            const uniqueKits = [];
            const seenKitNumbers = new Set();
            let returnQty = 0;
            let remarks = "";

            response.data.data.forEach(item => {
                if (item.kit_no && !seenKitNumbers.has(item.kit_no)) {
                    seenKitNumbers.add(item.kit_no);
                    uniqueKits.push({
                        id: item.kit_no,
                        kit_no: item.kit_no,
                        batch: item.batch
                    });
                }
                if (item.returned_qty) {
                    returnQty = Number(item.returned_qty);
                }
                if(item.return_remarks){
                    remarks = item.return_remarks
                }
            });

            setKitList(uniqueKits);
            setReturnQty(returnQty);
            setCrates(qtyToCrates(returnQty, Number(selectedIngredient?.std_qty_per_crate_theli) || 0));
            setRemarks(remarks);
        } catch (error) {
            console.error("Error fetching kit numbers:", error);
            toast.error("Failed to load kit numbers");
        } finally {
            setLoading(false);
        }
    };

    // The packets figure, not the litres one: this screen counts theli against crates, and the two
    // divisors are different numbers on the same crate.
    const stdQtyPerCrate = Number(selectedIngredient?.std_qty_per_crate_theli) || 0;

    // No. of crates = No. of Theli/Packet / Std. Qty. per Crate - Theli/Packets, and
    // No. of Theli/Packet = No. of crates * Std. Qty. per Crate - Theli/Packets.
    const handleTheliChange = (value) => {
        setReturnQty(value);
        setCrates(qtyToCrates(value, stdQtyPerCrate));
    };

    const handleCratesChange = (value) => {
        setCrates(value);
        setReturnQty(cratesToQty(value, stdQtyPerCrate));
    };

    // Recompute whichever box is the derived one for the given selection, leaving the typed one
    // alone. Used when the ingredient changes and when the selection is switched.
    const syncQtyFields = (selection) => {
        if (selection === "crates") {
            setReturnQty(cratesToQty(crates, stdQtyPerCrate));
        } else {
            setCrates(qtyToCrates(returnQty, stdQtyPerCrate));
        }
    };

    const handleReturnSelectionChange = (selection) => {
        setReturnSelection(selection);
        syncQtyFields(selection);
    };

    // const handleKitChange = (kitId) => {
    //     setSelectedKits(prevSelected => {
    //         if (prevSelected.includes(kitId)) {
    //             return prevSelected.filter(id => id !== kitId);
    //         } else {
    //             return [...prevSelected, kitId];
    //         }
    //     });
    // };

    const handleKitChange = (kit) => {
        setSelectedKits((prevSelected) => {
            const isSelected = prevSelected.some(item => item.kit_no === kit.kit_no);

            if (isSelected) {
                // Remove if already selected
                return prevSelected.filter(item => item.kit_no !== kit.kit_no);
            } else {
                // Add new selection with batch
                return [...prevSelected, { kit_no: kit.kit_no, batch: kit.batch }];
            }
        });
    };

    const validateForm = () => {
        const errors = {};

        if (!mealDate) errors.mealDate = "Meal date is required";
        if (!selectedIngredient) errors.ingredient = "Ingredient is required";
        if (!selectedSession) errors.session = "Session is required";
        // Crates cannot be turned into a packet count without the ingredient's crate size, so say so
        // rather than letting the empty box read as a missing quantity.
        if (selectedIngredient && returnSelection === "crates" && !stdQtyPerCrate) {
            errors.crates = "Std. Qty. per Crate - Theli / Packets is not set for this ingredient";
        } else if (returnSelection === "crates") {
            if (!crates || crates <= 0) errors.crates = "Valid number of crates is required";
        } else if (!returnQty || returnQty <= 0) {
            errors.returnQty = "Valid number of theli/packets is required";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fill all required fields");
            return;
        }

        const confirmSubmit = window.confirm("Are you sure you want to return this item?");
        if (!confirmSubmit) return;

        setLoading(true);
        try {
            // Prepare data for submission
            const issueData = {
                meal_date: mealDate,
                ingredient_id: selectedIngredient.id,
                session_id: selectedSession.id,
                menu_item_id: selectedMenuItem?.id || null,
                kit_numbers: selectedKits,
                returned_qty: returnQty,
                crates: crates,
                remarks: remarks
            };

            console.log("issuedata:", issueData);

            // Send issue data to the server
            const response = await axios.post(`${baseUrl}/dairy_return/submit`, issueData);

            if (response.data.status) {
                toast.success("Item issued successfully");
                // Reset form
                setSelectedMenuItem(null);
                setSelectedKits([]);
                setReturnSelection("theli");
                setReturnQty("");
                setCrates("");
                setRemarks("");
                setSelectedSession(null);
                setSelectedIngredient(null);
                setMealDate(today);
            } else {
                toast.error(response.data.msg || "Failed to issue item");
            }
        } catch (error) {
            console.error("Error creating issue:", error);
            toast.error("Failed to issue item");
        } finally {
            setLoading(false);
        }
    };

    const handleChangeMultiSelect = (selectedOptions) => {
        // Convert the selected options back to your kit objects
        const selected = selectedOptions ?
            kitList.filter(kit => selectedOptions.some(option => option.value === kit.id)) :
            [];
        setSelectedKits(selected);
    };

    const handleDropdownClick = (index) => {
        setActiveIndex(activeIndex === index ? null : index); // Toggle dropdown state
    };

    return (
        <div className="p-2 sm:p-4">
            {loading && <Loader />}
            <div className="flex justify-between items-center py-4 px-2 sm:px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
                    Dairy Store Return
                </h1>
                <Toaster position="top-center" reverseOrder={false} />
            </div>

            <div className="bg-white shadow-md rounded px-4 sm:px-8 pt-6 pb-8 mb-4">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                        {/* Meal Date */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="mealDate">
                                    Meal Date *
                                </label>
                                <div className="flex-1">
                                    <input
                                        id="mealDate"
                                        type="date"
                                        className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${formErrors.mealDate ? 'border-red-500' : ''}`}
                                        value={mealDate}
                                        onChange={(e) => setMealDate(e.target.value)}
                                    />
                                    {formErrors.mealDate && <p className="text-red-500 text-xs italic mt-1">{formErrors.mealDate}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Ingredient Name */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2 relative">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="ingredient">
                                    Ingredient Name *
                                </label>
                                <div className="relative flex-1">
                                    <Select
                                        {...menuAboveTable}
                                        id="ingredient"
                                        className={`${formErrors.ingredient ? "border-red-500" : ""}`}
                                        classNamePrefix="select"
                                        value={selectedIngredient ? {
                                            value: selectedIngredient.id,
                                            label: selectedIngredient.ingredient
                                        } : null}
                                        name="ingredient"
                                        onChange={(selected) => {
                                            setSelectedIngredient(
                                                selected ?
                                                    ingredientList.find(item => item.id === selected.value) :
                                                    null
                                            );
                                        }}
                                        options={ingredientList.map(item => ({
                                            value: item.id,
                                            label: item.ingredient
                                        }))}
                                        placeholder="Select Ingredient"
                                        onMenuOpen={() => handleDropdownClick(0)}
                                        onMenuClose={() => setActiveIndex(null)}
                                    />
                                    {formErrors.ingredient && <p className="text-red-500 text-xs italic mt-1">{formErrors.ingredient}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Session */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2 relative">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="session">
                                    Session *
                                </label>
                                <div className="relative flex-1">
                                    <Select
                                        {...menuAboveTable}
                                        id="session"
                                        className={`${formErrors.session ? 'border-red-500' : ''}`}
                                        classNamePrefix="select"
                                        value={selectedSession ? {
                                            value: selectedSession.id,
                                            label: selectedSession.name
                                        } : null}
                                        name="session"
                                        onChange={(selected) => {
                                            setSelectedSession(
                                                selected ?
                                                    sessionList.find(item => item.id === selected.value) :
                                                    null
                                            );
                                        }}
                                        options={sessionList.map(item => ({
                                            value: item.id,
                                            label: item.name
                                        }))}
                                        placeholder="Select Session"
                                        onMenuOpen={() => handleDropdownClick(1)}
                                        onMenuClose={() => setActiveIndex(null)}
                                    />
                                    {formErrors.session && <p className="text-red-500 text-xs italic mt-1">{formErrors.session}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Menu Item */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2 relative">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="menuItem">
                                    Menu Item
                                </label>
                                <div className="relative flex-1">
                                    <Select
                                        {...menuAboveTable}
                                        id="menuItem"
                                        className=""
                                        classNamePrefix="select"
                                        value={selectedMenuItem ? {
                                            value: selectedMenuItem.id,
                                            label: selectedMenuItem.item_name
                                        } : null}
                                        name="menuItem"
                                        onChange={(selected) => {
                                            setSelectedMenuItem(
                                                selected ?
                                                    menuItemList.find(item => item.id === selected.value) :
                                                    null
                                            );
                                        }}
                                        options={menuItemList.map(item => ({
                                            value: item.id,
                                            label: item.item_name
                                        }))}
                                        placeholder="Select Menu Item"
                                        onMenuOpen={() => handleDropdownClick(2)}
                                        onMenuClose={() => setActiveIndex(null)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Kit Selection */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2 relative">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="kitNumbers">
                                    Kit No.
                                </label>
                                <div className="relative flex-1">
                                    <Select
                                        {...menuAboveTable}
                                        id="kitNumbers"
                                        isMulti
                                        className={`shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${formErrors.kitNumbers ? 'border-red-500' : ''}`}
                                        classNamePrefix="select"
                                        value={selectedKits.map(kit => ({
                                            value: kit.id,
                                            label: kit.kit_no
                                        }))}
                                        name="kitNumbers"
                                        onChange={(selectedOptions) => {
                                            const selected = selectedOptions ?
                                                kitList.filter(kit => selectedOptions.some(option => option.value === kit.id)) :
                                                [];
                                            setSelectedKits(selected);
                                        }}
                                        options={kitList.length > 0 ?
                                            kitList.map(kit => ({
                                                value: kit.id,
                                                label: kit.kit_no
                                            })) :
                                            []
                                        }
                                        placeholder="Select Kit No."
                                        filterOption={(option, inputValue) => {
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                        noOptionsMessage={() => "No kit numbers available"}
                                    />
                                    {formErrors.kitNumbers && <p className="text-red-500 text-xs italic mt-1">{formErrors.kitNumbers}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Return selection: which of the two boxes below is typed into */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40">
                                    Return Selection *
                                </label>
                                <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:gap-6">
                                    <label className="flex items-center gap-2 text-gray-700" htmlFor="returnSelectionTheli">
                                        <input
                                            id="returnSelectionTheli"
                                            type="radio"
                                            name="returnSelection"
                                            value="theli"
                                            checked={returnSelection === "theli"}
                                            onChange={() => handleReturnSelectionChange("theli")}
                                        />
                                        No. of Theli / Packets
                                    </label>
                                    <label className="flex items-center gap-2 text-gray-700" htmlFor="returnSelectionCrates">
                                        <input
                                            id="returnSelectionCrates"
                                            type="radio"
                                            name="returnSelection"
                                            value="crates"
                                            checked={returnSelection === "crates"}
                                            onChange={() => handleReturnSelectionChange("crates")}
                                        />
                                        No. of Crates
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Theli/Packets and Crates -- the one not selected is filled in by formula */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center mb-2">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40" htmlFor="returnQty">
                                    Returned Qty. *
                                </label>
                                <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:gap-4">
                                    <div className="flex-1">
                                        <label className="block text-gray-600 text-sm mb-1" htmlFor="returnQty">
                                            Nos of Theli / Packet
                                        </label>
                                        <input
                                            id="returnQty"
                                            type="number"
                                            placeholder="Nos of Theli / Packet"
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${returnSelection === "theli" ? "" : "bg-gray-100"} ${formErrors.returnQty ? 'border-red-500' : ''}`}
                                            value={returnQty}
                                            onChange={(e) => handleTheliChange(e.target.value)}
                                            readOnly={returnSelection !== "theli"}
                                        />
                                        {formErrors.returnQty && <p className="text-red-500 text-xs italic mt-1">{formErrors.returnQty}</p>}
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-gray-600 text-sm mb-1" htmlFor="crates">
                                            No. of crates
                                        </label>
                                        <input
                                            id="crates"
                                            type="number"
                                            placeholder="No. of crates"
                                            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${returnSelection === "crates" ? "" : "bg-gray-100"} ${formErrors.crates ? 'border-red-500' : ''}`}
                                            value={crates}
                                            onChange={(e) => handleCratesChange(e.target.value)}
                                            readOnly={returnSelection !== "crates"}
                                        />
                                        {formErrors.crates && <p className="text-red-500 text-xs italic mt-1">{formErrors.crates}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-start mb-2">
                                <label className="block text-gray-700 font-bold mb-2 sm:mb-0 sm:w-40 sm:mt-2" htmlFor="remarks">
                                    Remarks
                                </label>
                                <div className="flex-1">
                                    <textarea
                                        id="remarks"
                                        placeholder="To enter (if any)"
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="flex items-center justify-center mt-4">
                            <button
                                type="submit"
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-8 sm:px-12 rounded focus:outline-none focus:shadow-outline w-full sm:w-auto"
                            >
                                SUBMIT
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default DairyReturnList;